import axios from 'axios';
import { notifications } from '@mantine/notifications';
import moment from 'moment';
import type { LinkedIssue, TaskBaselineEntry, TaskSnapshot, UnitActivityDetail } from '../stores/types';
import { isAuthErrorStatus } from './sessionExpiry';

/** Convert a string to camelCase (e.g. "My Custom Type" -> "myCustomType"). */
export function toCamelCase(str: string): string {
  if (!str || typeof str !== 'string') return str;
  const trimmed = str.trim();
  if (!trimmed) return trimmed;
  const words = trimmed.split(/[\s_-]+/).filter(Boolean);
  return words
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join('');
}

// Helper functions for converting between our Task model and dhtmlx Gantt format
export function parseDateSafe(dateStr: string | null): Date | undefined {
    if (!dateStr) return undefined;
    try {
        // Parse as local wall-clock time to avoid timezone drift.
        // Supports:
        // - YYYY-MM-DD
        // - YYYY-MM-DD HH:mm:ss
        // - YYYY-MM-DDTHH:mm:ss
        // - values with trailing timezone suffix (ignored for local planning math)
        const s = String(dateStr).trim();
        const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/);
        if (m) {
            const year = Number(m[1]);
            const month = Number(m[2]) - 1;
            const day = Number(m[3]);
            const hour = Number(m[4] ?? 0);
            const minute = Number(m[5] ?? 0);
            const second = Number(m[6] ?? 0);
            const local = new Date(year, month, day, hour, minute, second, 0);
            return isNaN(local.getTime()) ? undefined : local;
        }
        const date = new Date(s);
        return isNaN(date.getTime()) ? undefined : date;
    } catch {
        return undefined;
    }
}

export function formatDateForBackend(date: Date | string): string {
    // Preserve local wall-clock timestamps for planning interoperability.
    return moment(date).format('YYYY-MM-DD HH:mm:ss');
}

/**
 * Normalize incoming date-time strings to wall-clock format used in our DB/API payloads:
 * "YYYY-MM-DD HH:mm:ss" (no timezone suffix).
 */
export function toWallClockDateTime(value: string | null | undefined): string | null {
    if (!value) return null;
    const s = String(value).trim();
    if (!s) return null;

    // Keep the visible date-time part and drop timezone/offset suffix.
    const dt = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/);
    if (dt) {
        const sec = dt[4] ?? '00';
        return `${dt[1]} ${dt[2]}:${dt[3]}:${sec}`;
    }

    // Date-only input -> midnight wall-clock.
    const d = s.match(/^(\d{4}-\d{2}-\d{2})$/);
    if (d) {
        return `${d[1]} 00:00:00`;
    }

    // Last resort: parse and reformat to wall-clock.
    const parsed = parseDateSafe(s);
    return parsed ? formatDateForBackend(parsed) : null;
}

export function dependencyTypeToDhtmlx(type: string): string {
    // Handle both API format ("FS") and dhtmlx format ("0")
    // If already in dhtmlx format, return as-is
    if (/^[0-3]$/.test(type)) {
        return type;
    }

    const map: Record<string, string> = {
        'FS': '0', // Finish to Start
        'SS': '1', // Start to Start
        'FF': '2', // Finish to Finish
        'SF': '3', // Start to Finish
    };
    return map[type.toUpperCase()] || '0';
}

export function dependencyTypeFromDhtmlx(type: string): string {
    const map: Record<string, string> = {
        '0': 'FS',
        '1': 'SS',
        '2': 'FF',
        '3': 'SF',
    };
    return map[type] || 'FS';
}

const safeStringify = (obj: any) => {
    const seen = new WeakSet();
    return JSON.stringify(obj, (key, value) => {
        if (typeof value === "object" && value !== null) {
            if (seen.has(value)) return;
            seen.add(value);
        }
        return value;
    });
};

function extractMessageFromErrorPayload(text: string): string {
    const trimmed = text.trim();
    if (!trimmed) return '';
    try {
        const parsed = JSON.parse(trimmed) as Record<string, unknown>;
        const candidates = [
            parsed.message,
            parsed.error,
            parsed.detail,
            parsed.error_description,
            parsed.title,
        ];
        for (const candidate of candidates) {
            if (typeof candidate === 'string' && candidate.trim()) {
                return candidate.trim();
            }
        }
    } catch {
        // plain-text body
    }
    return trimmed;
}

/**
 * Resolve a user-facing API error message.
 * Handles JSON bodies and Blob bodies (common when responseType: 'blob').
 */
export async function resolveApiErrorMessage(
    error: unknown,
    fallback = 'An unexpected error occurred.',
): Promise<string> {
    if (!axios.isAxiosError(error)) {
        if (error instanceof Error && error.message.trim()) return error.message.trim();
        return fallback;
    }

    const data = error.response?.data;
    const statusText =
        typeof error.response?.statusText === 'string' ? error.response.statusText.trim() : '';

    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        try {
            const text = await data.text();
            const fromBody = extractMessageFromErrorPayload(text);
            if (fromBody) return fromBody;
        } catch {
            // fall through
        }
        if (statusText && !/^ok$/i.test(statusText) && !/^\d+$/.test(statusText)) {
            return statusText;
        }
        return fallback;
    }

    if (typeof data === 'string' && data.trim()) {
        return extractMessageFromErrorPayload(data) || fallback;
    }

    if (data && typeof data === 'object') {
        const record = data as Record<string, unknown>;
        for (const key of ['message', 'error', 'detail', 'error_description', 'title']) {
            const value = record[key];
            if (typeof value === 'string' && value.trim()) return value.trim();
        }
    }

    if (statusText && !/^ok$/i.test(statusText) && !/^\d+$/.test(statusText)) {
        return statusText;
    }

    if (typeof error.message === 'string' && error.message.trim()) {
        return error.message.trim();
    }

    return fallback;
}

export const showErrorNotification = (error: any, title = "Request Failed") => {
    try {
        if (axios.isAxiosError(error) && isAuthErrorStatus(error.response?.status)) {
            return;
        }

        const data = error?.response?.data;
        const looksLikeBlob =
            typeof Blob !== 'undefined' &&
            data instanceof Blob;

        if (looksLikeBlob || (data && typeof data?.text === 'function')) {
            void resolveApiErrorMessage(error).then((message) => {
                notifications.show({
                    title,
                    message,
                    color: 'red',
                });
            });
            return;
        }

        let message = "An unexpected error occurred.";

        if (data) {
            if (typeof data === "string") {
                message = extractMessageFromErrorPayload(data) || message;
            } else if (data.message) {
                message = data.message;
            } else {
                message = safeStringify(data);
            }
        } else if (error?.message) {
            message = error.message;
        } else {
            try {
                message = safeStringify(error.response.data);
            } catch (err) {
                console.error("Failed to stringify error.response.data", err);
                message = "An unexpected error occurred.";
            }
        }

        const statusText =
            typeof error?.response?.statusText === 'string'
                ? error.response.statusText.trim()
                : '';
        if (
            (!message || message === 'An unexpected error occurred.' || message === 'Request failed with status code 400') &&
            statusText &&
            !/^ok$/i.test(statusText)
        ) {
            message = statusText;
        }

        notifications.show({
            title,
            message: typeof message === "string" ? message : String(message),
            color: "red",
        });

    } catch (fatal) {
        console.error("Unexpected failure in showErrorNotification:", fatal);
    }
};


export function capitalizeNames(...args: string[]) {
    return args.map(a => `${a[0].toUpperCase()}${a.slice(1)}`).join('');
    // return `${a[0].toUpperCase()}${a.slice(1)}${b[0].toUpperCase()}${b.slice(1)}`;
}

/** Normalize unit-activity rows from API (camelCase or snake_case). */
export function normalizeUnitActivityDetails(raw: any): UnitActivityDetail[] | undefined {
    const arr = raw?.unitActivityDetails ?? raw?.unit_activity_details;
    if (!Array.isArray(arr) || arr.length === 0) return undefined;
    const out: UnitActivityDetail[] = [];
    for (const row of arr) {
        const id = row?.id != null && String(row.id).trim() !== '' ? String(row.id).trim() : '';
        if (!id) continue;
        out.push({
            id,
            unitOfMeasure: String(row.unitOfMeasure ?? row.unit_of_measure ?? '').trim(),
            activityTypeTitle: String(row.activityTypeTitle ?? row.activity_type_title ?? '').trim(),
        });
    }
    return out.length > 0 ? out : undefined;
}

/** Normalize linked DPR issues / hindrances from a planning API task row. */
export function normalizeLinkedIssues(raw: any): LinkedIssue[] {
    const arr = raw?.linkedIssues ?? raw?.linked_issues;
    if (!Array.isArray(arr) || arr.length === 0) return [];
    const out: LinkedIssue[] = [];
    for (const row of arr) {
        const ticketId =
            row?.ticketId != null && String(row.ticketId).trim() !== ''
                ? String(row.ticketId).trim()
                : row?.ticket_id != null && String(row.ticket_id).trim() !== ''
                  ? String(row.ticket_id).trim()
                  : '';
        if (!ticketId) continue;
        out.push({
            ticketId,
            title: String(row?.title ?? '').trim(),
            status: String(row?.status ?? '').trim(),
            statusCategory: String(row?.statusCategory ?? row?.status_category ?? '').trim(),
            priority: String(row?.priority ?? '').trim() || undefined,
            category: String(row?.category ?? '').trim(),
            pendingWith: String(row?.pendingWith ?? row?.pending_with ?? '').trim(),
            currentlyAffected: Boolean(row?.currentlyAffected ?? row?.currently_affected),
        });
    }
    return out;
}

/** Normalize task type string from API / meta to store keys (matches Gantt task type options). */
export function normalizeTaskType(value: string | null | undefined): string | null {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;
    const compact = trimmed.toLowerCase().replace(/[\s_-]+/g, '');

    if (compact === 'unknown') return 'unknown';
    if (compact === 'constructionactivity') return 'construction_activity';
    if (compact === 'location') return 'location';
    if (compact === 'phase') return 'phase';
    if (compact === 'preparatoryactivity') return 'preparatory_activity';

    return 'unknown';
}

type TaskNameSource = {
    taskName?: string | null;
    name?: string | null;
    task_name?: string | null;
    text?: string | null;
};

/**
 * Resolve display/storage task name: `taskName` first, then `name`, then API/dhtmlx aliases.
 */
export function resolveTaskName(task: TaskNameSource | null | undefined): string {
    if (!task) return '';
    for (const value of [task.taskName, task.name, task.task_name, task.text]) {
        if (value != null && String(value).trim() !== '') {
            return String(value).trim();
        }
    }
    return '';
}

/** Same as {@link resolveTaskName} but returns `null` when empty (for TaskSnapshot fields). */
export function resolveTaskNameOrNull(task: TaskNameSource | null | undefined): string | null {
    const name = resolveTaskName(task);
    return name === '' ? null : name;
}

/**
 * Normalize API baseline rows into TaskBaselineEntry[].
 * Tolerates camelCase / snake_case aliases from planning API.
 * Null start/finish stay null — never filled from the live schedule.
 */
export function normalizeBaselineInfo(raw: unknown): TaskBaselineEntry[] {
    if (!Array.isArray(raw)) return [];
    const result: TaskBaselineEntry[] = [];
    for (const entry of raw) {
        if (entry == null || typeof entry !== 'object') continue;
        const row = entry as Record<string, unknown>;
        const indexRaw = row.index ?? row.baselineIndex ?? row.baseline_index;
        const index = Number(indexRaw);
        if (!Number.isFinite(index)) continue;
        const startRaw = row.start ?? row.start_date ?? row.startDate ?? null;
        const finishRaw = row.finish ?? row.finish_date ?? row.finishDate ?? row.end_date ?? row.endDate ?? null;
        const durationRaw = row.duration;
        const workRaw = row.workPlanned ?? row.work_planned ?? row.work;
        const costRaw = row.costPlanned ?? row.cost_planned ?? row.cost;
        const durationNum = durationRaw == null || durationRaw === '' ? null : Number(durationRaw);
        const workNum = workRaw == null || workRaw === '' ? null : Number(workRaw);
        const costNum = costRaw == null || costRaw === '' ? null : Number(costRaw);
        const nameRaw = row.name ?? row.baselineName ?? row.baseline_name ?? null;
        const idRaw = row.id ?? null;
        const revisionRaw =
            row.approvedRevisionId ?? row.approved_revision_id ?? row.revisionId ?? row.revision_id ?? null;
        result.push({
            id: idRaw == null || String(idRaw).trim() === '' ? null : String(idRaw),
            index,
            name: nameRaw == null || String(nameRaw).trim() === '' ? null : String(nameRaw).trim(),
            start:
                startRaw == null || String(startRaw).trim() === ''
                    ? null
                    : toWallClockDateTime(String(startRaw)) ?? String(startRaw),
            finish:
                finishRaw == null || String(finishRaw).trim() === ''
                    ? null
                    : toWallClockDateTime(String(finishRaw)) ?? String(finishRaw),
            duration: durationNum != null && Number.isFinite(durationNum) ? durationNum : null,
            workPlanned: workNum != null && Number.isFinite(workNum) ? workNum : null,
            costPlanned: costNum != null && Number.isFinite(costNum) ? costNum : null,
            approvedRevisionId:
                revisionRaw == null || String(revisionRaw).trim() === ''
                    ? null
                    : String(revisionRaw),
        });
    }
    return result.sort((a, b) => a.index - b.index);
}

/** Own UOM from task meta. Blank is treated as unset so a parent unit can be inherited. */
function readMetaUom(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
}

/** Quantity from task meta. Missing stays null so display defaults are never stored. */
function readMetaQuantity(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return null;
}

/**
 * Map one planning API task row (same shape as allTasks items) to TaskSnapshot.
 * Used by Gantt load and by fetchTask refresh.
 */
export function mapPlanningApiTaskRowToSnapshot(t: any): TaskSnapshot {
    const meta = t?.meta;
    return {
        id: t.id,
        projectId: t.projectId,
        parentTaskId: t.parentTaskId || null,
        taskName: resolveTaskNameOrNull(t),
        wbs: t.wbs || null,
        srNo: t.srNo ?? t.sr_no ?? null,
        outlineLevel: t.outlineLevel || null,
        outlinePosition: t.outlinePosition ?? null,
        critical: t.critical || false,
        milestone: t.milestone || false,
        summary: t.summary || false,
        start: toWallClockDateTime(t.start),
        finish: toWallClockDateTime(t.finish),
        duration: t.duration || null,
        actualStart: toWallClockDateTime(t.actualStart ?? t.actual_start ?? null),
        actualFinish: toWallClockDateTime(t.actualFinish ?? t.actual_finish ?? null),
        actualDuration: t.actualDuration ?? t.actual_duration ?? null,
        uom: readMetaUom(meta?.uom),
        totalQuantity: readMetaQuantity(meta?.totalQuantity),
        achievedQuantity: readMetaQuantity(meta?.achievedQuantity),
        percentComplete: t.percentComplete || 0,
        baselineInfo: normalizeBaselineInfo(t.baselineInfo ?? t.baseline_info),
        taskType: normalizeTaskType(t.meta?.type ?? null),
        locationType: t.locationType || null,
        locationId: t.locationId ?? t.meta?.locationId ?? t.meta?.location_id ?? null,
        calendarId: t.calendarId ?? t.calendar_id ?? null,
        uniqueId: t.uniqueId ?? t.unique_id ?? null,
        taskMode: t.taskMode ?? t.task_mode ?? null,
        constraintType: t.constraintType ?? t.constraint_type ?? null,
        planningType: t.type ?? t.planningType ?? null,
        durationType: t.durationType ?? t.duration_type ?? null,
        unitActivityDetails: normalizeUnitActivityDetails(t) ?? null,
        notes: t.notes != null ? String(t.notes) : null,
        openIssueCount: Number(t.openIssueCount ?? t.open_issue_count ?? 0) || 0,
        linkedIssues: normalizeLinkedIssues(t),
        auto_scheduling: true,
        readonly: t.readonly || false,
    };
}

/** Unwrap one task from fetch/task (`{ data }`, array, or raw object). */
export function unwrapPlanningFetchTaskResponse(body: any, taskId?: string): any {
    if (body == null) return null;
    let payload = body.data ?? body;
    if (Array.isArray(payload)) {
        if (taskId) {
            const match = payload.find(
                (t) => String(t?.id ?? t?.taskId ?? t?.task_id ?? '') === String(taskId)
            );
            return match ?? payload[0] ?? null;
        }
        return payload[0] ?? null;
    }
    if (payload != null && typeof payload === 'object') {
        return payload;
    }
    return body;
}

export function planningFetchTaskRowId(row: any): string {
    return String(row?.id ?? row?.taskId ?? row?.task_id ?? '');
}

/**
 * Routine batched saves merge meta. Omit unknown type so a null does not wipe the stored type.
 * Quantity keys stay out; the quantity endpoint owns them.
 */
function buildRoutineTaskMeta(snapshot: { taskType?: string | null; locationId?: string | null }): {
    type?: string;
    locationId: string | null;
} {
    const meta: { type?: string; locationId: string | null } = {
        locationId: snapshot.locationId ?? null,
    };
    const taskType = typeof snapshot.taskType === 'string' ? snapshot.taskType.trim() : '';
    if (taskType) meta.type = taskType.toLowerCase();
    return meta;
}

/**
 * Transform TaskSnapshot (store format) to TaskPayload (API format)
 * Reverse of the transformation in ZustandGantt.tsx loadTasks()
 */
export function transformTaskSnapshotToPayload(snapshot: any): any {
    const payload: any = {
        id: snapshot.id,
        projectId: snapshot.projectId,
        parentTaskId: snapshot.parentTaskId,
        taskName: snapshot.taskName,
        wbs: snapshot.wbs,
        srNo: snapshot.srNo ?? null,
        outlineLevel: snapshot.outlineLevel,
        outlinePosition: snapshot.outlinePosition,
        critical: snapshot.critical,
        milestone: snapshot.milestone,
        summary: snapshot.summary,
        start: snapshot.start,
        finish: snapshot.finish,
        duration: snapshot.duration,
        actualStart: snapshot.actualStart ?? null,
        actualFinish: snapshot.actualFinish ?? null,
        actualDuration: snapshot.actualDuration ?? null,
        percentComplete: snapshot.percentComplete,
        baselineInfo: snapshot.baselineInfo,
        meta: buildRoutineTaskMeta(snapshot),
        taskType: snapshot.taskType,  // Keep in top level for backward compatibility
        locationType: snapshot.locationType,
    };
    if (Array.isArray(snapshot.unitActivityDetails) && snapshot.unitActivityDetails.length > 0) {
        payload.unitActivityDetails = snapshot.unitActivityDetails;
    }
    return payload;
}
